import { useMemo } from 'react'

import { useActiveBranchRecord } from '../../hooks'
import { useGetAuthzMeQuery } from '../authz/authz-api'
import type { Branch } from '../branches'
import { useGetIngestJobsPageQuery } from './ingest-api'
import { resolveJobBranchAdoptionOutcome, type BranchAdoptionOutcome } from './job-lifecycle'
import type { JobListItem } from './types'

const EMPTY_JOBS: JobListItem[] = []

const INGEST_SESSION_KINDS = new Set<JobListItem['kind']>([
  'structured',
  'plc',
  'robotics',
  'vision',
  'mtconnect',
  'pi',
  'canary',
  'experion',
  'unstructured'
])

/** Enough rows to find all ingest jobs sharing the active branch. */
const SESSION_PRESENCE_PAGE_SIZE = 50

export type IngestSession = Extract<
  JobListItem,
  {
    kind:
      | 'structured'
      | 'plc'
      | 'robotics'
      | 'vision'
      | 'mtconnect'
      | 'pi'
      | 'canary'
      | 'experion'
      | 'unstructured'
  }
>

export interface CurrentIngestSession {
  session: IngestSession | undefined
  branch: Branch | undefined
  outcome: BranchAdoptionOutcome | undefined
}

/** Actor jobs must never supply session identity for the changes bar. */
export function isAgentEditJob(job: JobListItem): boolean {
  return (job as { kind: string }).kind === 'agent_edit'
}

function isIngestSession(job: JobListItem): job is IngestSession {
  return INGEST_SESSION_KINDS.has(job.kind)
}

export function pickIngestSessions(jobs: JobListItem[]): IngestSession[] {
  return jobs
    .map((job, index) => ({ job, index }))
    .filter(
      (entry): entry is { job: IngestSession; index: number } =>
        isIngestSession(entry.job) && !isAgentEditJob(entry.job)
    )
    .sort((a, b) => {
      const submittedOrder = b.job.submittedAt.localeCompare(a.job.submittedAt)
      return submittedOrder === 0 ? a.index - b.index : submittedOrder
    })
    .map(({ job }) => job)
}

/**
 * Prefer a PLC zip umbrella when it shares the branch — children write the
 * log under the umbrella job id.
 */
export function preferSessionForBranch(
  sessions: readonly IngestSession[]
): IngestSession | undefined {
  if (sessions.length === 0) return undefined
  const zipUmbrella = sessions.find(
    (session) => session.kind === 'plc' && session.fileType === 'zip'
  )
  return zipUmbrella ?? sessions[0]
}

export function findCurrentIngestSession(
  branch: Branch | undefined,
  sessions: readonly IngestSession[]
): IngestSession | undefined {
  if (!branch) return undefined
  return preferSessionForBranch(sessions.filter((session) => session.branchId === branch.id))
}

const UNSTRUCTURED_TERMINAL_STATES = new Set(['done', 'error', 'cancelled', 'transformed'])

export type UnstructuredIngestSession = Extract<IngestSession, { kind: 'unstructured' }>

/**
 * Newest live unstructured job whose writes land on a different branch than
 * the one currently active. Used when Current Ingest is hidden — especially
 * protected `main` — so a parked `user_question` can still offer a swap.
 */
export function findOffBranchUnstructuredSession(
  branch: Branch | undefined,
  sessions: readonly IngestSession[]
): UnstructuredIngestSession | undefined {
  const activeId = branch?.id
  return sessions.find(
    (session): session is UnstructuredIngestSession =>
      session.kind === 'unstructured' &&
      Boolean(session.branchId) &&
      session.branchId !== activeId &&
      !UNSTRUCTURED_TERMINAL_STATES.has(session.state)
  )
}

/**
 * The ingest session associated with the active branch. Branch matching lives
 * here so every session-aware surface uses one rule.
 */
export function useCurrentIngestSession(): CurrentIngestSession {
  const branch = useActiveBranchRecord()
  const canReadIngest = useGetAuthzMeQuery().data?.permissions.includes('ingest/read') === true
  const { data } = useGetIngestJobsPageQuery(
    {
      kind: 'all',
      page: 0,
      pageSize: SESSION_PRESENCE_PAGE_SIZE,
      branchId: branch?.id
    },
    {
      skip: !canReadIngest || !branch?.id || branch.protected,
      refetchOnMountOrArgChange: true
    }
  )
  const sessions = useMemo(() => pickIngestSessions(data?.jobs ?? EMPTY_JOBS), [data?.jobs])
  const session = useMemo(() => findCurrentIngestSession(branch, sessions), [branch, sessions])
  const outcome = useMemo(
    () => (session ? resolveJobBranchAdoptionOutcome(session) : undefined),
    [session]
  )

  return useMemo(() => ({ session, branch, outcome }), [branch, outcome, session])
}

/**
 * Active branch plus the ingest session that supplies changes-bar identity (when
 * present). Paint uses this for branch-keyed folds — agent-only branches have no
 * session but still paint from the branch fold.
 */
export function useSessionPaintBranchContext(): Pick<CurrentIngestSession, 'session' | 'branch'> {
  const branch = useActiveBranchRecord()
  const { session } = useCurrentIngestSession()
  return useMemo(() => ({ session, branch }), [branch, session])
}
